import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Calendar, CreditCard, RefreshCw, Star, Award, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RenewalPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [mentorshipStatus, setMentorshipStatus] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Fetch user permissions and status
      const response = await fetch(`${backendUrl}/api/auth/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMentorshipStatus(data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleRenewMentorship = async (track) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      // Create checkout session for renewal
      const response = await fetch(`${backendUrl}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: track === 'apostolic-prophetic' ? 'leadership_track' : 'prophetic_track',
          success_url: `${window.location.origin}/payment-success`,
          cancel_url: `${window.location.origin}/renewal`
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendMentorship = () => {
    // For current members to extend their membership
    const currentTrack = mentorshipStatus?.mentorship_track || 'apostolic-prophetic';
    handleRenewMentorship(currentTrack);
  };

  const isMember = mentorshipStatus?.is_mentorship_member;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-purple-50 rounded-full px-4 py-2 mb-4">
            <RefreshCw className="w-4 h-4 text-purple-600" />
            <span className="text-purple-600 text-sm font-semibold">Membership Renewal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {isMember ? 'Extend Your Journey' : 'Continue Your Growth'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {isMember 
              ? 'Keep growing in your calling with another 12 months of apostolic mentorship'
              : 'Re-enroll to regain access to all mentorship features and continue your spiritual journey'
            }
          </p>
        </div>

        {/* Current Status (for members) */}
        {isMember && (
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Active Membership</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div>
                <div className="text-purple-200 text-sm mb-1">Current Track</div>
                <div className="text-xl font-semibold">
                  {mentorshipStatus?.mentorship_track === 'apostolic-prophetic' 
                    ? 'Apostolic-Prophetic Leadership' 
                    : 'Minstrel & Worship Leadership'}
                </div>
              </div>
              <div>
                <div className="text-purple-200 text-sm mb-1">Member Since</div>
                <div className="text-xl font-semibold">
                  {user?.joined_at ? new Date(user.joined_at).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-purple-200 text-sm mb-1">Total Points</div>
                <div className="text-xl font-semibold">{user?.points || 0} pts</div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Reminder */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Your Membership Includes
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Full Course Access</h3>
                <p className="text-gray-600 text-sm">Access to all video courses and learning materials</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Monthly Q&A Sessions</h3>
                <p className="text-gray-600 text-sm">One-on-one sessions with Pastor Obed Ansu</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Community Access</h3>
                <p className="text-gray-600 text-sm">Join our exclusive mentee community chat</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Progress Tracking</h3>
                <p className="text-gray-600 text-sm">Track your growth and earn achievements</p>
              </div>
            </div>
          </div>
        </div>

        {/* Renewal Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Apostolic-Prophetic Track */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-colors">
            <div className="h-2 bg-gradient-to-r from-purple-600 to-purple-700"></div>
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-purple-600 uppercase">Track 1</div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Apostolic–Prophetic Leadership
              </h3>
              
              <div className="mb-6">
                <div className="text-2xl font-bold text-gray-900">
                  From £120<span className="text-base font-normal text-gray-500">/month</span>
                </div>
                <div className="text-sm text-green-600 font-medium">or £1,200 upfront (Save £200)</div>
                <div className="text-sm text-gray-600 mt-1">12-Month Commitment</div>
              </div>

              <button
                onClick={() => navigate('/pricing?track=apostolic-prophetic')}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg shadow-purple-500/30 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>View Payment Plans</span>
              </button>
            </div>
          </div>

          {/* Minstrel & Worship Track */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-orange-200 hover:border-orange-400 transition-colors">
            <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-orange-600 uppercase">Track 2</div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Minstrel & Worship Leadership
              </h3>
              
              <div className="mb-6">
                <div className="text-2xl font-bold text-gray-900">
                  From £120<span className="text-base font-normal text-gray-500">/month</span>
                </div>
                <div className="text-sm text-green-600 font-medium">or £1,200 upfront (Save £200)</div>
                <div className="text-sm text-gray-600 mt-1">12-Month Commitment</div>
              </div>

              <button
                onClick={() => navigate('/pricing?track=minstrel-worship')}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg shadow-orange-500/30 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>View Payment Plans</span>
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">What happens when I renew?</h3>
              <p className="text-gray-600">
                Your membership is extended for another 12 months, giving you continued access to all mentorship features, courses, and community.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Can I switch tracks?</h3>
              <p className="text-gray-600">
                Yes! When renewing, you can choose a different track if you'd like to explore another area of ministry.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Will my progress be saved?</h3>
              <p className="text-gray-600">
                Absolutely. All your course progress, points, and achievements are permanently saved to your account.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">What payment methods are accepted?</h3>
              <p className="text-gray-600">
                We accept all major credit cards, debit cards, and digital payment methods through our secure Stripe payment processor.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Have questions about renewal?</p>
          <button
            onClick={() => navigate('/contact')}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:border-purple-600 hover:text-purple-600 transition-colors"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenewalPage;
